import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { CompanyType } from '@hum/types';
import { AdminSidebar } from './CompanySidebar';
import { AdminTitle } from './AdminTitle';
import { Sidebar } from './styles.pc';
import { CompanyOverview } from './CompanyOverview';
import { InvestorOverview } from './InvestorOverview';
import { CompanySyndication } from './tabs/Syndication/CompanySyndication';
import { AdminPage } from './outer';
import { format } from 'url';
import * as Icons from '@hum/icm-app/src/components/Icons';
import { Modal } from '@hum/design-system';
import { TabNavigator, Tab, MetaTitle } from '@hum/legacy-ui';
import {
  Button,
  ButtonType,
  Scrollable,
  Spinner,
  Text,
  TextType,
} from '@hum/ui-library';
import { AddPortfolioInvestor } from './AddPortfolioInvestor';
import { Users } from './tabs/Users';
import { useCompanyContact } from '@hum/icm-app/src/hooks/useCompanyContact';
import { useUrlQuery } from '@hum/common/src/hooks/useUrlQuery';
import { DataTab } from './tabs/Data';
import { Analytics, AnalyticsTab } from './tabs/Analytics';
import { UpdateCompanyInformation } from './UpdateCompanyInformation';
import { AdminLeftSidebar } from './LeftSidebar';
import { analytics } from '@hum/common';
import { useFlags } from '../../hooks/useFlags';
import {
  getCompany,
  getCompanyUuid,
  useApiMutation,
  useApiQuery,
} from '@hum/api';

dayjs.extend(relativeTime);

export const ADMIN_COMPANY_DETAIL_TAB_ID = 'admin-company-detail';

export const Admin = () => {
  const flags = useFlags();
  const history = useHistory();
  const [{ company_id }] = useUrlQuery();
  const companyId = company_id;
  const {
    data: company,
    isLoading: isLoadingCompany,
    isError: isErrorCompany,
  } = useApiQuery(getCompany, {
    params: { uuid: companyId },
    enabled: !!companyId,
  });
  const { mutate: getUuid, isLoading: isGettingUuid } = useApiMutation(
    getCompanyUuid,
    {
      onSuccess: (data) => {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        params.set('company_id', data.uuid);
        history.replace({ search: params.toString() });
      },
    }
  );
  const companyContact = useCompanyContact(companyId);

  const [companyUpdateModalOpen, setCompanyUpdateModal] = useState(false);
  const isCreditAnalystDemo = flags.enabled('demo:credit-analyst');
  const isCompanyDemo = flags.enabled('demo:company');
  const isPortfolioInvestorDemo = flags.enabled('demo:portfolio-investor');
  const isDemo =
    isCreditAnalystDemo || isCompanyDemo || isPortfolioInvestorDemo;

  const removeQuery = () => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    params.delete('update-company-information-tab');
    history.push(format({ search: params.toString() }));
  };
  const openCompanyUpdateForm = () => {
    setCompanyUpdateModal(true);
    // @ts-ignore
    analytics.track('admin:open_update_company_modal');
  };

  const onCloseUpdateCompany = () => {
    setCompanyUpdateModal(false);
    removeQuery();
  };

  const companyIsPortfolioInvestor =
    company?.type.includes(CompanyType.Investor) || false;
  const companyIsSyndicationInvestor =
    company?.type.includes(CompanyType.SyndicationInvestor) || false;
  const companyIsBalanceSheetPartner =
    company?.type.includes(CompanyType.BalanceSheetPartner) || false;
  const companyIsCompany = company?.type.includes(CompanyType.Company) || false;
  const companyIsAdmin = company?.type.includes(CompanyType.Admin) || false;

  // Add portfolio investor form
  const [portfolioInvestorModalOpen, setPortfolioInvestorModal] = useState(
    false
  );

  const openPortfolioInvestorForm = () => {
    setPortfolioInvestorModal(true);
  };

  const onClosePortfolioInvestor = () => {
    setPortfolioInvestorModal(false);
  };

  useEffect(() => {
    const isNumeric = !isNaN(Number(companyId));
    if (isNumeric) {
      getUuid({ companyId: Number(companyId) });
    }
  }, [companyId]);

  useEffect(() => {
    document.getElementById('hubspot-messages-iframe-container')?.remove();
  }, []);

  useEffect(() => {
    if (isDemo) {
      //eslint-disable-next-line
      window.location.assign(`${process.env.V2_BASE_URL}/`);
    }
  }, []);

  if (flags.error) {
    console.error('Flagsmith failed to load');
  }

  const renderContent = () => {
    if ((isLoadingCompany || isGettingUuid) && companyId !== '') {
      return <Spinner fullScreen />;
    }

    if (isErrorCompany) {
      return (
        <div
          className="flex flex-col justify-center items-center w-full h-full"
          data-testid="admin:error-companies-message"
        >
          <Text type={TextType.BODY_L}>
            We couldn’t find the company you’re looking for.
          </Text>
        </div>
      );
    }

    if (!companyId) {
      return (
        <div
          className="flex flex-col justify-center items-center w-full h-full"
          data-testid="admin:empty-companies-message"
        >
          <Text type={TextType.BODY_L}>
            Please, select a company to the left
          </Text>
        </div>
      );
    }

    if (companyId && company?.uuid) {
      return (
        <>
          <Scrollable className="flex-grow w-3/4 adminCompany">
            <div className="px-12 py-8 h-full">
              <div className="flex justify-between items-start mb-8">
                <AdminTitle
                  name={company.name}
                  website={company.website}
                  type={company.type}
                  tags={company.tags}
                  segment={company.application?.segment}
                  companyId={company.id}
                />
                <Button
                  type={ButtonType.QUIET}
                  testId="admin:update_company_button"
                  onClick={() => {
                    openCompanyUpdateForm();
                  }}
                >
                  <Icons.Edit />
                </Button>
              </div>

              <TabNavigator
                horizontal
                id={ADMIN_COMPANY_DETAIL_TAB_ID}
                data-testid="admin-page:main-area:company-tab-list"
              >
                {!companyIsAdmin && (
                  <Tab label="Overview" data-testid="admin:overview_tab">
                    <div data-testid="admin-page:main-area:overview">
                      {companyIsCompany && (
                        <CompanyOverview
                          companyContact={companyContact}
                          company={company}
                        />
                      )}
                      {(companyIsSyndicationInvestor ||
                        companyIsBalanceSheetPartner ||
                        companyIsPortfolioInvestor) && (
                        <InvestorOverview
                          company={company}
                          companyContact={companyContact.data}
                        />
                      )}
                    </div>
                  </Tab>
                )}
                {/* Note, this is rendered individually instead of in a fragment because the TabList component, when iterating, considers the React.Fragment element as a single element and cannot iterate it directly, breaking the index-based nature of this tabber */}

                {companyIsCompany && (
                  <Tab label="Data" data-testid="admin:data_tab">
                    <DataTab company={company} />
                  </Tab>
                )}
                <Tab label="Users" data-testid="admin:users_tab">
                  <Users />
                </Tab>

                {companyIsCompany && (
                  <Tab
                    label={({ activateTab }) => (
                      <AnalyticsTab activateTab={activateTab} />
                    )}
                    data-testid="admin:analytics_tab"
                  >
                    {company && <Analytics company={company} />}
                  </Tab>
                )}
                {companyIsCompany && (
                  <Tab label="Deals" data-testid="admin:syndication_tab">
                    <div data-testid="admin-page:main-area:syndication">
                      {companyIsCompany && (
                        <CompanySyndication company={company} />
                      )}
                    </div>
                  </Tab>
                )}
              </TabNavigator>
            </div>
          </Scrollable>
          {companyIsCompany && <AdminSidebar company={company} />}
        </>
      );
    }

    return null;
  };

  return (
    <>
      <MetaTitle>{company?.name || 'Select a company'}</MetaTitle>
      <AdminPage>
        <Sidebar testId="admin-page:side-bar">
          <>
            <div className="flex px-3 pt-3" style={{ minHeight: '52px' }}>
              <Button
                testId="add-fund-button"
                onClick={() => openPortfolioInvestorForm()}
                fullWidth
              >
                <Icons.Plus /> Add fund
              </Button>
            </div>
            {companyUpdateModalOpen && (
              <Modal
                onClose={onCloseUpdateCompany}
                testId="update-company-information-modal"
                header="Update company information"
                veryWide
                fullHeight
                scrollable
              >
                <UpdateCompanyInformation
                  company={company!}
                  onCancel={onCloseUpdateCompany}
                />
              </Modal>
            )}
            <AddPortfolioInvestor
              title="Add portfolio investor"
              onClose={onClosePortfolioInvestor}
              visible={portfolioInvestorModalOpen}
            />
          </>

          <AdminLeftSidebar currentCompanyUuid={companyId} />
        </Sidebar>
        <Scrollable className="relative w-full adminContent">
          <main
            className="flex flex-grow h-full"
            data-testid="admin-page:main-area"
          >
            {renderContent()}
          </main>
        </Scrollable>
      </AdminPage>
    </>
  );
};
